import { useEffect, useState } from 'react'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import { countsAsAvailable, type Category, type DayEntry } from '../../components/agent/scheduleDay'
import { supabase } from '../../lib/supabase'
import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { PAY_PERIOD, PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { getBoardRoster, type BoardRosterRow } from './boardRosterStore'
import { useSupervisorTeams } from './useSupervisorTeams'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import { COVERAGE_STATE_META, coverageStateFor } from './dailyAssignmentBoard'
import { qualifiedActual } from './coverageAnalysis'
import { applyDailyAssignments, overridesForDay } from './planningBoardDraftStore'
import { getPublishedPeriods } from './publishedScheduleStore'
import { getTeamLeaveRequests, type TeamLeaveRequest } from './leaveRequestQueue'
import { getQualificationsOn } from './supervisorDashboardStore'
import {
  getStaffingTargets,
  getTargetCatalog,
  resolveShiftTarget,
  resolveTarget,
  weekdayNameFor,
  type StaffingTarget,
  type TargetCatalog,
} from './staffingTargetStore'

/**
 * docs/design/journey-map.md's "Daily assignment board": the day's roster
 * for one date, on-duty personnel on one side, the team's assignments on
 * the other (the assignment catalog, getTargetCatalog) with a coverage
 * strip against the winning staffing target for the viewed date, shift,
 * team, and assignment (staffingTargetStore.ts's resolveTarget,
 * configured on SupervisorStaffingTargetsPage.tsx), and a Whole shift tile
 * when a target names no assignment (resolveShiftTarget), measured against
 * everyone available (work, and any status code that counts as available).
 * An assignment with no target reads "No target" rather than a made-up
 * requirement. Scoped to the current pay period alone. The roster is the selected team's real
 * employees and their schedule_entries (boardRosterStore.ts); the team
 * toggle (TeamSwitcher.tsx) lists the teams the signed-in supervisor owns,
 * per docs/product/personas.md's "A supervisor owns one or more teams";
 * a saved day is written into that team's planning board draft
 * (planningBoardDraftStore.ts, keyed by team), so switching teams never mixes
 * one team's changes into another's.
 *
 * A person is "on duty" when their date's own entry (base schedule, or
 * this board's own override) is a work entry; their current assignment is
 * that entry's detail when it names one of the team's assignments,
 * Unassigned otherwise. Reassigning keeps the entry's own shift time and
 * only changes the detail, whether by the "Assign to" select (the
 * keyboard-operable path, and the one every assistive technology can use)
 * or by dragging the row onto a group, native HTML5 drag and drop layered
 * on top rather than replacing the select. Edit day opens the fuller
 * six-status editor (CATEGORY_OPTIONS, the same set the planning board
 * applies) for changes beyond reassignment, such as pulling someone onto
 * leave for the day; there is no separate activity, sub-activity, or
 * comment field yet, so Edit day stops at status and, for Work,
 * assignment.
 *
 * Undo and the unsaved changes are both scoped to the viewed date rather than
 * the whole board: switching dates loads that date fresh as the planning
 * board's draft has it, and Undo is cleared along with it, the same boundary
 * the planning board's own Undo describes for itself. Navigating to another date or away from the page warns first if
 * the current date has unsaved changes, the same guard the planning board
 * uses for its own Dashboard link.
 */

const CATEGORY_OPTIONS: { key: Category; label: string; patchLabel: string }[] = [
  { key: 'work', label: 'Work', patchLabel: '0700–1500' },
  { key: 'off', label: 'Off', patchLabel: 'Regular day off' },
  { key: 'leave', label: 'Leave', patchLabel: 'Annual leave' },
  { key: 'training', label: 'Training', patchLabel: '0800–1600 · Training' },
  { key: 'detail', label: 'Detail', patchLabel: 'Detailed out' },
  { key: 'unavailable', label: 'Unavailable', patchLabel: 'Unavailable' },
]

// The pay period's own date skeleton: shared by every team in this field
// office, so it does not depend on which team is currently in view.
const DATES = PAY_PERIOD_DATES

// The actual current date, not a fixed date the placeholder pilot data is
// anchored to, the same as AgentCalendarPage.tsx's own REAL_TODAY_ISO.
// Falls back to the period's first day once the real date has moved
// outside Pay Period 19's own range.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
const TODAY_INDEX = Math.max(
  0,
  DATES.findIndex((date) => date.date === REAL_TODAY_ISO),
)

type Override = { category: Category; label: string; detail?: string }

function overridesEqual(a: Map<string, Override>, b: Map<string, Override>): boolean {
  if (a.size !== b.size) return false
  for (const [key, value] of a) {
    const other = b.get(key)
    if (!other || other.category !== value.category || other.label !== value.label || other.detail !== value.detail) {
      return false
    }
  }
  return true
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  )
}

export default function SupervisorDailyAssignmentsPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const { teams, currentTeamId, teamsLoaded, switchTeam: switchToTeam } = useSupervisorTeams(userId)
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  // The selected team's real employees and schedules, its assignments and
  // shift, and each employee's qualifications on the viewed date.
  const [roster, setRoster] = useState<BoardRosterRow[]>([])
  const [catalog, setCatalog] = useState<TargetCatalog>({ shift: null, assignments: [], qualifications: [] })
  // A published pay period is final until it is reopened on the planning board, so
  // nothing on this board can change it.
  const [periodLocked, setPeriodLocked] = useState(false)
  const [qualifications, setQualifications] = useState<Map<string, Set<string>>>(new Map())
  // Which team the roster and assignments above were read for, so the board
  // is not shown empty in the meantime, and never shows one team's people
  // under another team's name while a switch is still loading.
  const [loadedTeamId, setLoadedTeamId] = useState<string | null>(null)
  const boardLoaded = loadedTeamId === currentTeamId
  const [dateIndex, setDateIndex] = useState(TODAY_INDEX)
  const [overrides, setOverrides] = useState<Map<string, Override>>(new Map())
  const [savedOverrides, setSavedOverrides] = useState<Map<string, Override>>(new Map())
  // The day as this team's planning board draft has it, which the roster's own
  // schedule is read through.
  const [planningOverrides, setPlanningOverrides] = useState<Map<string, Override>>(new Map())
  const [undoStack, setUndoStack] = useState<Map<string, Override>[]>([])
  const [editingFor, setEditingFor] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [saveFailed, setSaveFailed] = useState(false)
  // Which `${userId}:${teamId}:${date}` the state above was last loaded
  // for, so a date, a team switch, or the first-known userId that has not
  // been loaded yet can reset that state during render rather than in an
  // effect, per React's own "adjusting state when a prop changes"
  // guidance: an effect here would still be correct, but would cost an
  // extra render on every date or team change for no benefit.
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const [leaveRequests, setLeaveRequests] = useState<TeamLeaveRequest[]>([])

  const selectedDate = DATES[dateIndex]
  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''

  if (userId && selectedDate && `${userId}:${currentTeamId}:${selectedDate.date}` !== loadedFor) {
    // A day opens as this team's planning board draft has it, with no changes of
    // its own: what was saved here earlier is in that draft already.
    const loaded = new Map<string, Override>()
    setOverrides(loaded)
    setSavedOverrides(new Map(loaded))
    setPlanningOverrides(overridesForDay(userId, currentTeamId, dateIndex))
    setUndoStack([])
    setEditingFor(null)
    setSavedAt(null)
    setSaveFailed(false)
    setLoadedFor(`${userId}:${currentTeamId}:${selectedDate.date}`)
  }

  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          setTargets(await getStaffingTargets())
          setLeaveRequests(await getTeamLeaveRequests())
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!currentTeamId) return
    let cancelled = false
    Promise.all([
      getBoardRoster(currentTeamId, PAY_PERIOD_DATES, PAY_PERIOD_20_DATES),
      getTargetCatalog(currentTeamId),
      getPublishedPeriods(currentTeamId),
    ]).then(
      ([loadedRoster, loadedCatalog, locked]) => {
        if (cancelled) return
        setRoster(loadedRoster.current)
        setCatalog(loadedCatalog)
        setPeriodLocked(locked.has(PAY_PERIOD_DATES[0]?.date ?? ''))
        setLoadedTeamId(currentTeamId)
      },
    )
    return () => {
      cancelled = true
    }
  }, [currentTeamId])

  const viewedDate = selectedDate?.date
  useEffect(() => {
    if (!viewedDate) return
    let cancelled = false
    getQualificationsOn(viewedDate).then((loaded) => {
      if (!cancelled) setQualifications(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [viewedDate])

  const hasUnsavedChanges = !overridesEqual(overrides, savedOverrides)

  useEffect(() => {
    if (!hasUnsavedChanges) return
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  function baseEntryFor(employeeId: string): DayEntry | undefined {
    if (!selectedDate) return undefined
    const day = roster.find((member) => member.id === employeeId)?.days.find((entry) => entry.date === selectedDate.date)
    // A day the planning board has changed and not yet published reads as changed
    // here too; its own category decides availability, not the status code it was.
    const planned = planningOverrides.get(employeeId)
    return day && planned ? { ...day, ...planned, state: undefined, countsAsAvailable: undefined } : day
  }

  // An approved FULL-DAY leave request covering the viewed date, the same
  // priority ahead of a board override SupervisorPlanningBoardPage.tsx's
  // own entryFor gives it: someone whose leave a supervisor has approved
  // for the whole day is not on duty here either, regardless of what this
  // board's own draft still says for them. A partial-day approval is
  // excluded the same reason SupervisorPlanningBoardPage.tsx's own
  // appliedFullDayLeave is: they are still on duty for the rest of the
  // day, so this must not remove them from the on-duty roster below.
  function approvedLeaveFor(employeeId: string): { label: string } | undefined {
    if (!selectedDate) return undefined
    const match = leaveRequests.find(
      (item) =>
        item.state === 'approved' && item.fullDay && item.employeeId === employeeId && item.dates.includes(selectedDate.date),
    )
    return match ? { label: match.leaveType } : undefined
  }

  function entryFor(employeeId: string): DayEntry | undefined {
    const base = baseEntryFor(employeeId)
    if (!base) return undefined
    const appliedLeave = approvedLeaveFor(employeeId)
    if (appliedLeave) return { ...base, category: 'leave', label: appliedLeave.label, detail: 'Full day', state: 'approved', countsAsAvailable: false }
    const override = overrides.get(employeeId)
    if (!override) return base
    // The override's own category decides availability, not the status code the
    // day started as.
    return { ...base, category: override.category, label: override.label, detail: override.detail, state: undefined, countsAsAvailable: undefined }
  }

  function workAssignmentFor(employeeId: string): string | undefined {
    const current = entryFor(employeeId)
    if (current?.category === 'work' && current.detail && catalog.assignments.some((option) => option.name === current.detail)) {
      return current.detail
    }
    return catalog.assignments[0]?.name
  }

  function commitOverride(employeeId: string, next: Override) {
    if (periodLocked) return
    setUndoStack((stack) => [...stack.slice(-49), new Map(overrides)])
    const updated = new Map(overrides)
    updated.set(employeeId, next)
    setOverrides(updated)
    setSavedAt(null)
  }

  function assignTo(employeeId: string, assignmentName: string | null) {
    const current = entryFor(employeeId)
    if (!current) return
    commitOverride(employeeId, { category: 'work', label: current.label, detail: assignmentName ?? undefined })
  }

  function applyStatus(employeeId: string, option: (typeof CATEGORY_OPTIONS)[number]) {
    const detail = option.key === 'work' ? workAssignmentFor(employeeId) : undefined
    commitOverride(employeeId, { category: option.key, label: option.patchLabel, detail })
    setEditingFor(null)
  }

  function undo() {
    if (undoStack.length === 0) return
    const previous = undoStack[undoStack.length - 1]
    setOverrides(previous)
    setUndoStack((stack) => stack.slice(0, -1))
  }

  function saveDraft() {
    if (periodLocked) return
    if (!userId || !selectedDate) {
      setSaveFailed(true)
      return
    }
    const entries = [...overrides.entries()].map(([employeeId, override]) => ({ employeeId, ...override }))
    if (!applyDailyAssignments(userId, currentTeamId, dateIndex, entries)) {
      setSaveFailed(true)
      return
    }
    setSaveFailed(false)
    setSavedAt(new Date())
    setSavedOverrides(new Map(overrides))
    setPlanningOverrides(overridesForDay(userId, currentTeamId, dateIndex))
    setUndoStack([])
  }

  function confirmDiscard(): boolean {
    return !hasUnsavedChanges || window.confirm('Leave this day without saving? Unsaved changes will be lost.')
  }

  function goToDate(nextIndex: number) {
    if (!confirmDiscard()) return
    setDateIndex(Math.min(Math.max(nextIndex, 0), DATES.length - 1))
  }

  // Each team a supervisor owns keeps its own draft for the viewed date
  // (the render-time load above, keyed by `${userId}:${teamId}:${date}`),
  // so switching teams is a fresh load rather than a filter; asks first if
  // the team being left has unsaved changes, the same confirmDiscard the
  // date-navigation buttons use.
  function switchTeam(nextTeamId: string) {
    if (nextTeamId === currentTeamId) return
    if (!confirmDiscard()) return
    switchToTeam(nextTeamId)
  }

  if (!sessionReady || !selectedDate || (userId && !teamsLoaded) || (currentTeamId !== '' && !boardLoaded)) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  const teamAssignments = catalog.assignments

  const onDuty = roster.map((member) => ({ id: member.id, name: member.name, entry: entryFor(member.id) })).filter(
    (person): person is { id: string; name: string; entry: DayEntry } => person.entry?.category === 'work',
  )

  const assigned = new Map<string, { id: string; name: string; entry: DayEntry }[]>(
    teamAssignments.map((assignment) => [assignment.name, []]),
  )
  const unassigned: { id: string; name: string; entry: DayEntry }[] = []
  for (const person of onDuty) {
    const detail = person.entry.detail
    if (detail && assigned.has(detail)) assigned.get(detail)?.push(person)
    else unassigned.push(person)
  }

  // A target naming no assignment is a minimum for the whole shift, measured
  // against everyone available this date whatever their assignment: work, and
  // any status code that counts as available, such as training.
  const shiftTarget = resolveShiftTarget(targets, {
    date: selectedDate.date,
    weekday: weekdayNameFor(selectedDate.date),
    shift: catalog.shift?.name ?? '',
    team: teamName,
  })
  const shiftAvailable = roster
    .map((member) => ({ id: member.id, entry: entryFor(member.id) }))
    .filter((person) => person.entry !== undefined && countsAsAvailable(person.entry))
  const shiftActual = qualifiedActual(shiftAvailable, shiftTarget?.qualification, qualifications)
  const groups: { name: string; people: { id: string; name: string; entry: DayEntry }[] }[] = [
    ...teamAssignments.map((assignment) => ({ name: assignment.name, people: assigned.get(assignment.name) ?? [] })),
    { name: 'Unassigned', people: unassigned },
  ]

  function handleDrop(event: React.DragEvent<HTMLElement>, assignmentName: string | null) {
    event.preventDefault()
    const employeeId = event.dataTransfer.getData('text/plain')
    if (employeeId) assignTo(employeeId, assignmentName)
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#daily-assignments-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <div className="print:hidden">
        <AppHeader initials={identity?.initials ?? ''} />
      </div>

      <main id="daily-assignments-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav beforeLeave={confirmDiscard} />
        <div className="mx-auto flex w-full max-w-[1100px] flex-1 flex-col gap-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-h2 font-extrabold">Daily Assignment Board</h1>
              <p className="mt-1 text-small text-ink-muted">
                {teamName} &middot; {PAY_PERIOD.label}, {PAY_PERIOD.range}
              </p>
              {periodLocked && (
                <p role="status" className="mt-1 text-small font-semibold text-ink-muted">
                  {PAY_PERIOD.label} is published, so its assignments cannot be changed here. Reopen it on the planning board to change them.
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 print:hidden">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={undo}
                  disabled={undoStack.length === 0 || periodLocked}
                  className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-40"
                >
                  Undo
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                >
                  Print
                </button>
                <button
                  type="button"
                  onClick={saveDraft}
                  disabled={periodLocked}
                  className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:pointer-events-none disabled:opacity-40"
                >
                  Save Draft
                </button>
              </div>
              {saveFailed ? (
                <p role="alert" className="text-caption font-semibold text-error">
                  Could not save. Storage may be unavailable in this browser.
                </p>
              ) : (
                savedAt && (
                  <p className="text-caption text-ink-subtle">
                    Saved {savedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                )
              )}
            </div>
          </div>

          <div className="print:hidden">
            <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 print:hidden">
              <button
                type="button"
                onClick={() => goToDate(dateIndex - 1)}
                disabled={dateIndex === 0}
                aria-label="Previous day"
                className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
              >
                <ChevronLeftIcon />
              </button>
              <button
                type="button"
                onClick={() => goToDate(dateIndex + 1)}
                disabled={dateIndex === DATES.length - 1}
                aria-label="Next day"
                className="flex size-11 items-center justify-center rounded-control border border-line-strong text-ink-muted disabled:opacity-40"
              >
                <ChevronRightIcon />
              </button>
            </div>
            <p className="text-h3 font-extrabold">
              {selectedDate.weekday}, {selectedDate.month} {selectedDate.day}
              {selectedDate.date === REAL_TODAY_ISO && (
                <span className="ml-2 align-middle text-caption font-bold text-primary">Today</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => goToDate(TODAY_INDEX)}
              className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3.5 text-small font-bold text-ink-muted print:hidden"
            >
              Today
            </button>
          </div>

          <div role="group" aria-label="Coverage" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {shiftTarget && (
              <div className="rounded-surface border border-line bg-canvas p-3">
                <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">Whole shift</p>
                <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">
                  {shiftActual}
                  <span className="text-ink-subtle"> / {shiftTarget.minimumHeadcount}</span>
                </p>
                <p className={`text-caption font-bold ${COVERAGE_STATE_META[coverageStateFor(shiftActual, shiftTarget.minimumHeadcount)].className}`}>
                  {COVERAGE_STATE_META[coverageStateFor(shiftActual, shiftTarget.minimumHeadcount)].label}
                </p>
              </div>
            )}
            {teamAssignments.map((assignment) => {
              const target = resolveTarget(targets, {
                date: selectedDate.date,
                weekday: weekdayNameFor(selectedDate.date),
                shift: catalog.shift?.name ?? '',
                team: teamName,
                assignment: assignment.name,
              })
              const actual = qualifiedActual(assigned.get(assignment.name) ?? [], target?.qualification, qualifications)
              const required = target?.minimumHeadcount
              const meta = required === undefined ? undefined : COVERAGE_STATE_META[coverageStateFor(actual, required)]
              return (
                <div key={assignment.id} className="rounded-surface border border-line bg-canvas p-3">
                  <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{assignment.name}</p>
                  <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">
                    {actual}
                    {required !== undefined && <span className="text-ink-subtle"> / {required}</span>}
                  </p>
                  <p className={`text-caption font-bold ${meta?.className ?? 'text-ink-subtle'}`}>
                    {meta?.label ?? 'No target'}
                  </p>
                </div>
              )
            })}
          </div>

          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <div
                key={group.name}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleDrop(event, group.name === 'Unassigned' ? null : group.name)}
                className="rounded-surface border border-line bg-canvas"
              >
                <div className="border-b border-line px-3 py-2">
                  <h2 className="text-small font-bold">
                    {group.name} <span className="text-ink-subtle">({group.people.length})</span>
                  </h2>
                </div>
                {group.people.length === 0 ? (
                  <EmptyState>Nobody is here right now. Drag a person in, or assign them below.</EmptyState>
                ) : (
                  <ul>
                    {group.people.map((person) => (
                      <li key={person.id} className="border-b border-line px-3 py-2 last:border-b-0">
                        <div
                          draggable={!periodLocked}
                          onDragStart={(event) => {
                            event.dataTransfer.setData('text/plain', person.id)
                            event.dataTransfer.effectAllowed = 'move'
                          }}
                          className="flex flex-wrap items-center gap-2"
                        >
                          <span className="min-w-[150px] text-small font-bold">{person.name}</span>
                          <span className="text-caption text-ink-muted [font-variant-numeric:tabular-nums]">
                            {formatScheduleLabel(person.entry.label, format)}
                          </span>
                          <label
                            htmlFor={`assign-${person.id}`}
                            className="ml-auto flex items-center gap-1.5 text-caption font-bold text-ink-muted print:hidden"
                          >
                            Assign to
                            <select
                              id={`assign-${person.id}`}
                              disabled={periodLocked}
                              value={group.name === 'Unassigned' ? '' : group.name}
                              onChange={(event) =>
                                assignTo(person.id, event.target.value === '' ? null : event.target.value)
                              }
                              className="min-h-11 rounded-control border border-line-strong px-2 text-caption"
                            >
                              <option value="">Unassigned</option>
                              {teamAssignments.map((assignment) => (
                                <option key={assignment.id} value={assignment.name}>
                                  {assignment.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button
                            type="button"
                            aria-expanded={editingFor === person.id}
                            disabled={periodLocked}
                            onClick={() => setEditingFor((current) => (current === person.id ? null : person.id))}
                            className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2 disabled:pointer-events-none disabled:opacity-40 print:hidden"
                          >
                            Edit day
                          </button>
                        </div>
                        {editingFor === person.id && (
                          <div
                            role="group"
                            aria-label={`Edit ${person.name}'s day`}
                            className="mt-2 flex flex-wrap gap-2"
                          >
                            {CATEGORY_OPTIONS.map((option) => (
                              <button
                                key={option.key}
                                type="button"
                                onClick={() => applyStatus(person.id, option)}
                                className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-caption font-bold hover:bg-surface-2"
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
